import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  BonosService,
  Campana,
  FilaTablero,
  TableroResumen,
} from 'src/app/shared/services/bonos.service';

/**
 * El tablero de una campaña: cifras arriba, reclamos abajo.
 *
 * **Los rechazos se listan junto a los aceptados**, y esa es la mitad del valor
 * de esta pantalla. Un tablero que sólo muestra quién cobró deja invisible al
 * que probó veinte veces: el ataque se ve en los rechazos, no en los pagos, y
 * verlo mientras la campaña está viva es la diferencia entre cortar hoy y
 * enterarse el mes que viene.
 *
 * El documento llega **enmascarado del backend**, no se enmascara acá. Es
 * deliberado: si la máscara fuera del front, el número completo viajaría en la
 * respuesta y estaría a un clic en las herramientas del navegador. El operador
 * necesita reconocer a la persona y ver que el mismo documento apareció en dos
 * cuentas; no necesita el número entero.
 *
 * El ritmo se muestra contra el umbral del freno, y eso es para verlo **antes**
 * de que salte: cuando ya se pausó, lo único que queda es decidir si se
 * reanuda. Con el ritmo a la vista todavía se puede decidir algo.
 */
@Component({
  selector: 'app-campana-tablero',
  templateUrl: './campana-tablero.component.html',
  styleUrls: ['./campana-tablero.component.sass'],
})
export class CampanaTableroComponent implements OnInit {
  @Input() campana!: Campana;
  @Output() volver = new EventEmitter<void>();

  cargando = true;
  cargandoTabla = false;
  error = '';

  resumen: TableroResumen | null = null;
  filas: FilaTablero[] = [];

  page = 1;
  limit = 50;
  total = 0;

  /** Default: se incluyen. Ver la doc de la clase. */
  incluirRechazos = true;

  constructor(private bonos: BonosService) {}

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  private async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    try {
      const [resumen, reclamos] = await Promise.all([
        this.bonos.tablero(this.campana.bonoCampanaId),
        this.bonos.reclamos(this.campana.bonoCampanaId, {
          page: 1,
          limit: this.limit,
          incluirRechazos: this.incluirRechazos,
        }),
      ]);
      this.resumen = resumen;
      this.filas = reclamos.items;
      this.total = reclamos.total;
      this.page = reclamos.page;
    } catch (e: any) {
      this.error = e?.error?.message || 'No pudimos cargar el tablero de esta campaña.';
    } finally {
      this.cargando = false;
    }
  }

  async refrescar(): Promise<void> {
    await this.cargar();
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  async irAPagina(page: number): Promise<void> {
    if (page < 1 || page > this.totalPaginas || this.cargandoTabla) return;
    this.cargandoTabla = true;
    try {
      const r = await this.bonos.reclamos(this.campana.bonoCampanaId, {
        page,
        limit: this.limit,
        incluirRechazos: this.incluirRechazos,
      });
      this.filas = r.items;
      this.total = r.total;
      this.page = r.page;
    } catch {
      this.filas = [];
      this.total = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  /** Al cambiar el filtro se vuelve a la página 1: la 7 puede no existir más. */
  async alternarRechazos(): Promise<void> {
    this.incluirRechazos = !this.incluirRechazos;
    await this.irAPagina(1);
  }

  // ------------------------------------------------------------- el ritmo

  /**
   * Qué tan cerca está del freno, en porcentaje del umbral.
   *
   * `null` si la campaña no tiene freno configurado — que no debería pasar,
   * porque el formulario pone el del parámetro, pero una campaña vieja podría.
   */
  get ritmoPct(): number | null {
    const r = this.resumen?.ritmo;
    if (!r || !r.umbral) return null;
    return Math.min(100, Math.round((r.tomadosEnVentana / r.umbral) * 100));
  }

  /** Cerca del freno: vale mirar ahora, no cuando ya se pausó. */
  get ritmoAlto(): boolean {
    const p = this.ritmoPct;
    return p !== null && p >= 60;
  }

  get avanceCupo(): number {
    const u = this.resumen?.unidades;
    if (!u || !u.total) return 0;
    return Math.min(100, Math.round((u.usadas / u.total) * 100));
  }

  // ------------------------------------------------------------- la tabla

  aceptado(f: FilaTablero): boolean {
    return f.resultado === 'ACEPTADO';
  }

  /**
   * Una fila con señales. Se destaca la fila entera y no sólo el chip: lo que
   * se busca acá es el patrón, y un patrón se ve en el bloque, no en el detalle.
   */
  conSenal(f: FilaTablero): boolean {
    return (f.senales?.length ?? 0) > 0;
  }

  /**
   * Cómo se lee cada motivo de rechazo para un operador.
   *
   * El enum es del motor y no está escrito para mostrarse. Y la traducción no
   * es la del cliente: al cliente se le dice qué hacer, al operador se le dice
   * **qué chequeo frenó**, que es lo que necesita para decidir si es un cliente
   * confundido o alguien probando.
   */
  motivoTexto(motivo: string | null): string {
    if (!motivo) return '';
    const textos: Record<string, string> = {
      DEMASIADOS_INTENTOS: 'freno de ritmo',
      CUENTA_NO_APROBADA: 'compliance sin aprobar',
      CODIGO_INVALIDO: 'código inexistente',
      FUERA_DE_VIGENCIA: 'fuera de vigencia',
      CAMPANA_PAUSADA: 'campaña pausada o módulo apagado',
      CUENTA_EN_SOBREGIRO: 'cuenta en sobregiro',
      FUERA_DEL_ALCANCE: 'otro país',
      PUBLICO_NO_APLICA: 'no cumple el público',
      EXCLUIDO_POR_REFERIDO: 'ya tiene bono de referido',
      DOCUMENTO_YA_USADO: 'documento ya cobró',
      YA_TIENE_BONO_ACTIVO: 'ya tiene un bono en curso',
      CONFLICTO_DE_INTERES: 'creó o activó la campaña',
      SIN_UNIDADES: 'cupo agotado',
      TECHO_MENSUAL: 'techo del mes',
    };
    return textos[motivo] ?? motivo.toLowerCase().replace(/_/g, ' ');
  }

  /**
   * Los motivos que hay que mirar, y los que son ruido.
   *
   * `CODIGO_INVALIDO` y `FUERA_DE_VIGENCIA` son ruido esperable: las capturas
   * del código circulan para siempre. `DOCUMENTO_YA_USADO` repetido y
   * `CONFLICTO_DE_INTERES` no son ruido: el primero es alguien reintentando con
   * varias cuentas, el segundo es un operador probando su propia campaña.
   */
  motivoSospechoso(motivo: string): boolean {
    return (
      motivo === 'DOCUMENTO_YA_USADO' ||
      motivo === 'CONFLICTO_DE_INTERES' ||
      motivo === 'DEMASIADOS_INTENTOS'
    );
  }

  estadoTexto(estado: string | null): string {
    if (!estado) return '';
    return estado.toLowerCase();
  }

  atras(): void {
    this.volver.emit();
  }
}
